<template>
  <v-card class="mx-auto" elevation="16" max-width="100vw">
    <v-card-item>
      <v-card-title> Correos</v-card-title>
      <v-card-subtitle>
        <v-row>
          <v-col cols="12"> Lista de Correos de Contacto </v-col>
        </v-row>

        <div class="search-bar">
          <input
            v-model="search"
            type="text"
            placeholder="Buscar..."
            class="custom-input"
          />
          <button class="custom-btn" @click="onSearch">Buscar</button>
        </div>
      </v-card-subtitle>
    </v-card-item>
    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="messagesContent"
        :loading="loading"
        :search="search"
        :items-per-page="itemsPerPage"
        :page="currentPage"
        :server-items-length="totalItems"
        :items-per-page-options="[10, 25, 50, 100]"
        item-value="id"
        @update:options="onOptionsUpdate"
        class="elevation-1"
      >
        <template #item="{ item }">
          <tr>
            <td>{{ item.id }}</td>
            <td>
              <span :class="getStatusBadge(item.status).class">
                {{ getStatusBadge(item.status).text }}
              </span>
            </td>
            <td>
              {{ item.title || "" }} {{ item.name || "" }}
              {{ item.lastname || "" }}
            </td>
            <td>{{ item.email }}</td>
            <td>{{ item.company }}</td>
            <td>{{ item.zip || "" }} {{ item.city }} {{ item.country }}</td>
            <td>{{ formatDateTime(item.created_at) }}</td>
            <td>
              <v-tooltip text="Ver Correo" location="top">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    class="btn-accion"
                    icon
                    @click="seeMore(item)"
                  >
                    <v-icon class="fa-solid fa-eye"></v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="Responder Correo" location="top">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    class="btn-accion"
                    icon
                    @click="sentMessageBack(item)"
                  >
                    <v-icon class="fa-solid fa-reply"></v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
            </td>
          </tr>
        </template>
        <template v-slot:bottom>
          <v-toolbar flat color="white" class="v-toolbar__content">
            <div class="content-select-items">
              <label for=""># items</label>
              <select
                v-model="itemsPerPage"
                @change="onItemsPerPageChange"
                class="native-select"
              >
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </select>
            </div>

            <v-spacer></v-spacer>

            <span class="text-caption me-2 content-select-items">
              Página {{ currentPage }} de
              {{ Math.ceil(totalItems / itemsPerPage) }}
            </span>
            <v-pagination
              v-model="currentPage"
              :length="Math.ceil(totalItems / itemsPerPage)"
              :total-visible="5"
              @update:modelValue="onPageChange"
              class="custom-pagination"
            />
          </v-toolbar>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
  <ModalShowMessage
    v-model="dialog"
    :correo="selectedMessage"
    @close="dialog = false"
  />
  <ModalSentMessageBack
  v-model:modelValueBack="dialogBack"
  :correo="selectedMessageBack"
  @mensaje-enviado="loadItems"
/>

</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { getMessages } from "../services/message-service";
import { getRoleByToken, formatDate } from "@/kernel/utils";
import ModalShowMessage from "../components/ModalShowMessage.vue";
import ModalSentMessageBack from "../components/ModalSentMessageBack.vue";

const messages = ref({
  content: [],
  totalElements: 0,
  page: 1,
  size: 25,
});

const itemsPerPage = ref(25);
const loading = ref(false);
const search = ref("");
const role = ref(null);
const dialog = ref(false);
const selectedMessage = ref(null);
const selectedMessageBack = ref(null);
const dialogBack = ref(false);
const currentPage = ref(1);
const totalItems = ref(0);

const onOptionsUpdate = async (options) => {
  currentPage.value = options.page;
  itemsPerPage.value = options.itemsPerPage;
  await loadItems();
};

const headers = ref([
  { title: "#", key: "id" },
  { title: "Estado", key: "status" },
  { title: "Nombre", key: "fullname" },
  { title: "Correo", key: "email" },
  { title: "Empresa", key: "company" },
  { title: "Dirección", key: "direccion" },
  { title: "Recibido el", key: "created_at" },
  { title: "", key: "actions", sortable: false },
]);

const messagesContent = computed(() => messages.value.content || []);

const formatDateTime = (date) => {
  return formatDate(date);
};

const onSearch = () => {
  currentPage.value = 1; // Resetea a la primera página al buscar
  loadItems();
};

const onPageChange = () => {
  loadItems();
};

onMounted(async () => {
  loadItems();
  role.value = await getRoleByToken();
});

const loadItems = async () => {
  loading.value = true;
  try {
    const params = {
      page: currentPage.value,
      limit: itemsPerPage.value,
      search: search.value,
    };
    const result = await getMessages(params);
    if (result && result.status === 200) {
      messages.value.content = result.data.contacts;
      messages.value.totalElements = result.data.total;
      totalItems.value = result.data.total;
    }
  } catch (error) {
    console.error("Error al cargar contactos:", error);
  } finally {
    loading.value = false;
  }
};

const seeMore = (message) => {
  selectedMessage.value = message;
  dialog.value = true;
};

const sentMessageBack = (message) => {
  selectedMessageBack.value = message;
  dialogBack.value = true;
};

const getStatusBadge = (status) => {
  switch (status) {
    case "SENT":
      return { text: "Enviado", class: "badge-sent" };
    case "PENDING":
      return { text: "Pendiente", class: "badge-pending" };
    case "SEEN":
      return { text: "Visto", class: "badge-seen" };
    case "REJECTED":
      return { text: "Rechazado", class: "badge-rejected" };
    case "ACCEPTED":
      return { text: "Aceptado", class: "badge-accepted" };
    default:
      return { text: "Desconocido", class: "badge-default" };
  }
};
</script>

<style scoped>
.badge-active {
  background-color: yellowgreen;
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
}

.badge-inactive {
  background-color: red;
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
}

.badge-unknown {
  background-color: orange;
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
}

.badge-default {
  background-color: blue;
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
}


.fa-trash:hover {
  color: red;
}
.fa-file:hover {
  color: rgb(34, 99, 127);
}
.fa-eye:hover {
  color: rgb(79, 127, 34);
}
.fa-circle-check:hover {
  color: green;
}
.btn-add-user {
  background-color: black;
  color: white;
}

.btn-add-user:hover {
  background-color: orange;
  color: #000;
}

/* badges */
.badge-sent {
  background-color: yellowgreen; /* Verde */
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
}

.badge-seen {
  background-color: orange; /* Azul */
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
}

.badge-rejected {
  background-color: #f44336; /* Rojo */
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
}

.badge-accepted {
  background-color: yellowgreen; /* Naranja */
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
}

.badge-default {
  background-color: grey;
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
}
.badge-pending {
  background-color: #ff9800;
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
}

/* search bar */
.search-bar {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  padding: 16px;
}

.custom-input {
  padding: 8px 12px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 6px;
  width: 300px;
}

.custom-btn {
  background-color: orange;
  color: white;
  padding: 16px 16px;
  font-weight: bold;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.custom-btn:hover {
  background-color: darkorange;
}

.custom-pagination {
  background-color: white !important;
  padding: 8px 0;
  margin-left: -24px;
  width: 100%;
  overflow-x: auto;
}

.custom-pagination >>> .v-pagination__item {
  border-radius: 30% !important;
  background-color: #f5f5f5;
  color: #333;
  transition: all 0.3s;
}

.custom-pagination >>> .v-pagination__item--is-active {
  background-color: orange !important;
  color: white !important;
}

.v-toolbar__content {
  width: 100%;
  flex-wrap: nowrap;
  overflow-x: auto;
  display: flex;
  gap: 8px;
  padding: 8px 0;
}
.custom-pagination {
  margin: 16px 0;
}

/* Mejora visual del v-select */
.content-select-items {
  display: inline-flex;
  white-space: nowrap;
  align-items: center;
  gap: 10px;
  margin-left: 60px;
  max-width: 100%;
}
.native-select {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #ccc;
  background-color: #f5f5f5;
  font-size: 14px;
  color: #333;
  transition: all 0.3s ease;
  outline: none;
  cursor: pointer;
}

.native-select:hover {
  border-color: orange;
}

.native-select:focus {
  border-color: darkorange;
  box-shadow: 0 0 5px rgba(255, 165, 0, 0.5);
}

/* RESPONSIVE ADAPTATIONS */
@media screen and (max-width: 768px) {
  .search-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .custom-input {
    width: 100%;
    margin-bottom: 10px;
  }

  .custom-btn {
    width: 100%;
    padding: 12px;
  }

  .native-select {
    width: 100%;
    margin: 10px 0;
  }

  .v-toolbar__content {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .custom-pagination {
    justify-content: center;
    margin-top: 10px;
  }
  .content-select-items {
    margin-left: 12px;
    margin-right: 12px;
  }
}
</style>
